import { App, Button, Card, Flex, Tag, theme, Tooltip, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faCarSide, faLocationDot, faMoneyBillWave, faPencilAlt, faUser } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '@shared/hooks/useLoading';
import { getVehicleServiceTypeDisplay } from '@shared/utils/display/vehicleDisplayUtils';
import { getBalance, getQuotationPrice } from 'Pages/DataGatewayPage/routerForm/utils';
import { formatCurrency } from 'Utils/numberUtils';
import { HeaderSkeleton } from './HeaderSkeleton';
import { VerticalDivider } from './VerticalDivider';
import { InfoItem } from './InfoItem';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { MONEY_COLOR } from '@shared/constants/colors';
import { ROUTES } from '@/App/Router/routes';

const { Text, Paragraph } = Typography;

export const QuotationsComparatorHeader = ({ quotation, loading }) => {
  const { token: themeToken } = theme.useToken();
  const editQuotationLoading = useLoading();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const externalSession = localStorage.getItem('external_session') === 'true';

  if (loading || !quotation.id) return <HeaderSkeleton />

  const handleEditQuotation = async () => {
    editQuotationLoading.start();
    let balance = 0;
    let quotationPrice = 0;

    try {
      balance = await getBalance();
      quotationPrice = await getQuotationPrice(false);
    } catch (error) {
      notification.error({
        message: <Text strong>No fue posible validar si tiene saldo suficiente para cotizar</Text>,
        description: getErrorMessage(error),
      });
      editQuotationLoading.stop();
      return;
    }

    // Si el saldo es mayor o igual al precio se dirige a la pantalla de edición
    if ((balance >= quotationPrice)) {
      navigate(ROUTES.QUOTATIONS.UPDATE(quotation.id));
    } else {
      notification.error({
        message: <Text strong>Saldo insuficiente</Text>,
        description: 'No cuenta con saldo suficiente para cotizar después de editar la cotización.',
      });
    }

    editQuotationLoading.stop();
  };

  return (
    <Card
      title={
        <Flex justify='center' align='center' gap='middle' wrap>
          <Flex gap='middle' align='center' flex={1}>
            <Tag
              style={{ fontSize: '1.1em', margin: 0, padding: 14, borderRadius: 8, lineHeight: 0 }}
              color={themeToken.colorPrimary}
            >
              <FontAwesomeIcon icon={faCarSide} />
            </Tag>
            <Flex vertical align='start'>
              {!externalSession && (
                <Tooltip title='Editar cotización'>
                  <Button
                    color='primary'
                    variant='text'
                    icon={<FontAwesomeIcon icon={faPencilAlt} />}
                    loading={editQuotationLoading.isLoading}
                    onClick={handleEditQuotation}
                    style={{ display: 'flex', flexDirection: 'row-reverse', fontSize: '1.1em', paddingInline: 8, marginInline: -8, border: 'none' }}
                  >
                    <Text strong style={{ fontSize: 'inherit' }}>
                      Cotización #{quotation.number}
                    </Text>
                  </Button>
                </Tooltip>
              )}
              <Paragraph type='secondary' ellipsis={{ rows: 2 }} style={{ marginBottom: 0, fontWeight: 'normal' }}>
                {quotation.vehicle_data.placa}<VerticalDivider />
                {quotation.vehicle_data.marca} {quotation.vehicle_data.referencia}
                {quotation.vehicle_data.is_new && <><VerticalDivider /><Tag color='blue' style={{ margin: 0 }}>Nuevo</Tag></>}
              </Paragraph>
            </Flex>
          </Flex>
          <Tag
            color={themeToken.colorPrimary}
            style={{ margin: 0, padding: '4px 16px', borderRadius: 8 }}
          >
            {quotation.vehicle_data.clase} {getVehicleServiceTypeDisplay(quotation.vehicle_data.servicio)}
          </Tag>
        </Flex>
      }
      style={{
        border: `1px solid ${themeToken.colorPrimary}66`, // 40% de opacidad al color primario
        boxShadow: `0 1px 3px ${themeToken.colorPrimary}4d`, // 30% de opacidad
      }}
      styles={{
        header: {
          backgroundColor: `${themeToken.colorPrimary}12`, // Se agrega un 7% de opacidad al color primario
          borderBottomColor: 'inherit', // Hereda el color del borde definido en style['border']
          padding: 16,
        },
        title: {
          whiteSpace: 'wrap',
        },
        body: {
          padding: 16,
        },
      }}
    >
      <Flex wrap justify='space-around' style={{ columnGap: 32, rowGap: 16 }}>
        <InfoItem icon={faCalendar} label='Modelo'>
          {quotation.vehicle_data.modelo}
        </InfoItem>
        <InfoItem icon={faLocationDot} label='Ciudad de circulación'>
          {quotation.vehicle_data.ciudad_circulacion_name}
        </InfoItem>
        <InfoItem icon={faUser} label='Tomador'>
          {quotation.client_data.first_name} {quotation.client_data.last_name}
        </InfoItem>
        <InfoItem icon={faUser} label='Documento'>
          {quotation.client_data.document_type} {quotation.client_data.document_number}
        </InfoItem>
        <InfoItem icon={faMoneyBillWave} iconColor={MONEY_COLOR} label='Valor asegurado'>
          {formatCurrency(quotation.vehicle_data.valor_asegurado)}
        </InfoItem>
      </Flex>
    </Card>
  );
};